import type { SourceView } from "../shared/types";
import { GUEST_AUDIENCE, SIGNED_IN_AUDIENCE, STAFF_AUDIENCE, effectiveAudiences, viewerAudiences } from "../shared/audience-presets";

/** The access choices of the Knowledge section; "custom" opens the audience chips. */
export const ACCESS_PRESETS = ["everyone", "signed_in", "staff", "custom"] as const;
export type AccessPreset = (typeof ACCESS_PRESETS)[number];
type FixedPreset = Exclude<AccessPreset, "custom">;

const PRESET_AUDIENCES: Readonly<Record<FixedPreset, readonly number[]>> = {
  everyone: [GUEST_AUDIENCE],
  signed_in: [SIGNED_IN_AUDIENCE],
  staff: [STAFF_AUDIENCE],
};

/** Audience codes a fixed preset writes to the source (`PATCH /v1/sources/{id}`). */
export function audiencesForPreset(preset: FixedPreset): number[] {
  return [...PRESET_AUDIENCES[preset]];
}

/**
 * Which choice describes a source's audiences. Anything that includes Public (1) is open to
 * guests, whatever else is listed; exactly Members (3) or exactly Staff (4) are the other two;
 * every other set (prospects, mixes) is custom.
 */
export function accessPresetOf(audiences: readonly number[]): AccessPreset {
  if (audiences.includes(GUEST_AUDIENCE)) return "everyone";
  const only = audiences.length === 1 ? audiences[0] : undefined;
  if (only === SIGNED_IN_AUDIENCE) return "signed_in";
  if (only === STAFF_AUDIENCE) return "staff";
  return "custom";
}

type VisibilitySource = Pick<SourceView, "id" | "audiences" | "status">;

/** True when a visitor with these (already capped) audiences reads the source. */
export function readableBy(source: Pick<SourceView, "audiences">, audiences: readonly number[]): boolean {
  return source.audiences.some((code) => audiences.includes(code));
}

export interface VisibilitySummary {
  guests: number;
  signedIn: number;
}

/**
 * "Guests can see n · Signed-in users can see n": this agent's linked, ready sources that a
 * guest / a signed-in visitor reads once the agent's own audience limit is applied.
 */
export function visibilitySummary(sources: readonly VisibilitySource[], linked: ReadonlySet<string>, cap: readonly number[]): VisibilitySummary {
  const guest = effectiveAudiences(viewerAudiences(GUEST_AUDIENCE), cap);
  const signedIn = effectiveAudiences(viewerAudiences(SIGNED_IN_AUDIENCE), cap);
  const used = sources.filter((source) => linked.has(source.id) && source.status === "ready");
  return {
    guests: used.filter((source) => readableBy(source, guest)).length,
    signedIn: used.filter((source) => readableBy(source, signedIn)).length,
  };
}

/** The limit narrows what visitors read: it leaves out Public or Members. */
export function isCapNarrowing(cap: readonly number[]): boolean {
  return !cap.includes(GUEST_AUDIENCE) || !cap.includes(SIGNED_IN_AUDIENCE);
}

/** Linked ids after ticking / unticking one source, order kept and no duplicates. */
export function toggledIds(current: readonly string[], sourceId: string, on: boolean): string[] {
  const rest = current.filter((id) => id !== sourceId);
  return on ? [...rest, sourceId] : rest;
}
